import { useState, ChangeEvent } from "react"
import {
  Button,
  Dialog,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUser, faPlus } from "@fortawesome/free-solid-svg-icons"

import {
  OptionProps,
  Selected,
  Contact,
} from "../../../../utils/safetyPlanUtils"
import { colors } from "../../../../utils/theme"
import useSafetyPlanOptionStyles from "./useSafetyPlanOptionStyles"

/**
 * Displays a list of contacts already added to a safety plan option, and
 * provides a button that opens a modal dialog for adding a new contact
 * (first name, optional last name, and phone number). Validates that a first
 * name and a recognisable phone number are provided before saving.
 *
 * @param option    - The `ResponseOption` whose id is used to look up and persist contacts.
 * @param onClick   - Callback invoked with `(id, [{ first, last, phone }], type)` on save.
 * @param type      - The category type string passed through to `onClick`.
 * @param selection - The current list of selected items, used to derive already-saved contacts.
 */
export default function ContactOption({
  option,
  onClick,
  type,
  selection,
}: OptionProps) {
  const el = selection.find((el: Selected) => el.id === option.id)
  const [openContact, setOpenContact] = useState(false)
  let values: Contact[] = []
  if (el !== undefined) {
    const { value } = el
    values = value as Contact[]
  }
  const handleOpenContact = () => {
    setOpenContact(true)
  }

  const handleCloseContact = () => {
    setOpenContact(false)
    setFirst("")
    setLast("")
    setPhone("")
  }

  const [first, setFirst] = useState("")
  const [last, setLast] = useState("")
  const [phone, setPhone] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  const handleFirstName = (event: ChangeEvent<HTMLInputElement>) => {
    setFirst(event.target.value)
    setErrorMessage("")
  }

  const handleLastName = (event: ChangeEvent<HTMLInputElement>) => {
    setLast(event.target.value)
    setErrorMessage("")
  }

  const handlePhone = (event: ChangeEvent<HTMLInputElement>) => {
    setPhone(event.target.value)
    setErrorMessage("")
  }

  const handleSave = () => {
    if (first.trim() === "") {
      setErrorMessage("Please provide a first name")
      return
    }

    const trimmedPhone = phone.trim()
    // Simple validation: only digits and delimiters (parentheses, hyphens, plus, spaces, dots)
    // Must contain at least one digit
    if (!/^[\d\s()\-+.]+$/.test(trimmedPhone) || !/\d/.test(trimmedPhone)) {
      setErrorMessage("Please provide a valid phone number")
      return
    }
    onClick(option.id, [{ first, last, phone: trimmedPhone }], type)
    setOpenContact(false)
    setErrorMessage("")
    setFirst("")
    setLast("")
    setPhone("")
  }

  const contactOptionStyles = useStyles()
  const sharedStyles = useSafetyPlanOptionStyles()
  const classes = { ...contactOptionStyles, ...sharedStyles }
  return (
    <>
      <Dialog open={openContact} onClose={handleCloseContact}>
        <div className={classes.modalContainer}>
          <div className={classes.modalBody}>
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  color: colors.primary.shadow,
                }}
              >
                <FontAwesomeIcon icon={faUser}></FontAwesomeIcon>
              </div>
              <Typography variant="h2" className={classes.addContactText}>
                Add a Contact
              </Typography>
            </div>
            <div id="info" className={classes.contactInfoContainer}>
              {errorMessage && (
                <div style={{ color: "red", fontSize: "14px" }}>
                  {errorMessage}
                </div>
              )}
              <div className={classes.modalField}>
                <TextField
                  id="contact-first-name"
                  label="First Name"
                  value={first}
                  onChange={handleFirstName}
                  variant="outlined"
                  placeholder=""
                  style={{ width: "100%" }}
                />
              </div>
              <div className={classes.modalField}>
                <TextField
                  id="contact-last-name"
                  label="Last Name (Optional)"
                  value={last}
                  onChange={handleLastName}
                  variant="outlined"
                  placeholder=""
                  style={{ width: "100%" }}
                />
              </div>
              <div className={classes.modalField}>
                <TextField
                  id="contact-phone-number"
                  label="Phone Number"
                  value={phone}
                  onChange={handlePhone}
                  variant="outlined"
                  placeholder=""
                  style={{ width: "100%" }}
                />
              </div>
            </div>
            <div className={classes.modalFooter}>
              <Button
                onClick={handleCloseContact}
                className={classes.cancelModalBtn}
                disableElevation
              >
                <Typography
                  className={classes.modalBtnTxt}
                  style={{ color: colors.primary.shadow }}
                >
                  Cancel
                </Typography>
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                color="primary"
                className={classes.saveModalBtn}
                disableElevation
              >
                <Typography
                  className={classes.modalBtnTxt}
                  style={{ color: colors.neutral.white }}
                >
                  Save
                </Typography>
              </Button>
            </div>
          </div>
        </div>
      </Dialog>
      {values.map((item: Contact, index: number) => (
        <div className={classes.optionContainer} key={index.toString()}>
          <Typography className={classes.optionContactTxt}>
            {item.first} {item.last}
          </Typography>
          <Typography className={classes.optionTxt}>{item.phone}</Typography>
        </div>
      ))}
      <Button
        className={classes.addContactModalBtn}
        id={option.id}
        onClick={handleOpenContact}
      >
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <FontAwesomeIcon icon={faPlus} className={classes.plusColor} />
          <Typography className={classes.btnText}>Add Contact</Typography>
        </div>
      </Button>
    </>
  )
}

const useStyles = makeStyles(() => ({
  contactInfoContainer: {
    display: "flex",
    padding: "24px 16px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "24px",
    alignSelf: "stretch",
    borderRadius: "8px",
    border: `1px solid ${colors.safetyPlan.grayscale.black10}`,
    background: colors.safetyPlan.cardSecondaryBackground,
  },
  addContactText: {
    color: colors.safetyPlan.ink,
    lineHeight: "40px",
  },
  optionContactTxt: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: "20px",
    color: colors.safetyPlan.midnight,
    flex: "1 0 0",
  },
  addContactModalBtn: {
    height: "52px",
    padding: "4px 32px",
    alignSelf: "stretch",
    borderRadius: "200px",
    border: `2px solid  ${colors.primary.shadow}`,
    background: colors.safetyPlan.grayscale.white85,
    textTransform: "none",
  },
}))
